import { useCallback, useEffect, useRef, useState } from 'react';

export const useRowScroll = () => {
  const listContainerRef = useRef<HTMLDivElement>(null);
  const listItemRefs = useRef<Array<HTMLDivElement | null>>([]);

  const [visibleFrom, setVisibleFrom] = useState(0);
  const [visibleTo, setVisibleTo] = useState(0);
  const [maxLeft, setMaxLeft] = useState(true);
  const [maxRight, setMaxRight] = useState(true);
  const [hasScrollableContent, setHasScrollableContent] = useState(false);

  const DPR = (globalThis as unknown as { devicePixelRatio?: number }).devicePixelRatio ?? 1;
  const EPS = Math.max(0.5, 2 / DPR);

  const recompute = useCallback(() => {
    const container = listContainerRef.current;
    if (!container) return;

    const c = container.getBoundingClientRect();
    const { scrollLeft, clientWidth, scrollWidth } = container;

    let from = -1;
    let to = -1;

    listItemRefs.current.forEach((el, idx) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      const overlap = Math.min(r.right, c.right - EPS) - Math.max(r.left, c.left + EPS);
      const visible = overlap > Math.min(r.width, c.width) * 0.5;
      if (visible) {
        if (from === -1) from = idx;
        to = idx;
      }
    });

    if (from === -1) {
      const i = listItemRefs.current.findIndex((el) => {
        if (!el) return false;
        const r = el.getBoundingClientRect();
        return r.left >= c.left - EPS;
      });
      from = i === -1 ? 0 : i;
      to = from;
    }

    setVisibleFrom(from);
    setVisibleTo(to);

    setMaxLeft(scrollLeft <= EPS);
    setMaxRight(scrollLeft + clientWidth >= scrollWidth - EPS);
    setHasScrollableContent(scrollWidth > clientWidth + EPS);
  }, [EPS]);

  useEffect(() => {
    const el = listContainerRef.current;
    if (!el) return;

    const onScroll = () => recompute();
    const onResize = () => recompute();

    el.addEventListener('scroll', onScroll, { passive: true });
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onResize);
    }

    let ro: ResizeObserver | undefined;
    const hasRO = typeof window !== 'undefined' && 'ResizeObserver' in window;
    if (hasRO) {
      ro = new window.ResizeObserver(onResize);
      ro?.observe(el);
    }

    recompute();

    // eslint-disable-next-line consistent-return
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', onResize);
      }
      ro?.disconnect();
    };
  }, [recompute]);

  const scrollToIndex = useCallback((idx: number) => {
    const container = listContainerRef.current;
    const el = listItemRefs.current[idx];
    if (!container || !el) return;
    container.scrollTo({ left: el.offsetLeft, behavior: 'smooth' });
  }, []);

  const onNavigationPrev = useCallback(() => {
    const prev = Math.max(visibleFrom - 1, 0);
    scrollToIndex(prev);
  }, [visibleFrom, scrollToIndex]);

  const onNavigationNext = useCallback(() => {
    const next = Math.min(visibleFrom + 1, listItemRefs.current.length - 1);
    scrollToIndex(next);
  }, [visibleFrom, scrollToIndex]);

  return {
    listContainerRef,
    listItemRefs,
    visibleFrom,
    visibleTo,
    maxLeft,
    maxRight,
    onNavigationPrev,
    onNavigationNext,
    hasScrollableContent,
  };
};
